import * as React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import * as Yup from 'yup';
import {
	Formik, Form, Field, FieldProps, FormikHelpers,
} from 'formik';
import Input from 'antd/lib/input';

import Captcha from '@common/react/components/Forms/Captcha';
import Button from '@common/react/components/Forms/Button';
import { emailValidator, simpleStringValidator } from '@common/react/utils/validationHelpers';

import { request } from '@app/components/Api';
import { FormikField } from '@app/components/Forms/FormikField';

type PageProps = RouteComponentProps;

interface FormValues {
	email: string;
	captcha: string;
}

const validationSchema = Yup.object().shape({
	email: emailValidator,
	captcha: simpleStringValidator,
});

interface ComponentState {
	code: string;
	success: boolean;
	message: string;
	isLoading: boolean;
	counter: number;
}

export class Register extends React.Component<PageProps, ComponentState> {
	state = {
		code: '',
		success: false,
		message: '',
		isLoading: false,
		counter: 0,
	};

	constructor(props: PageProps) {
		super(props);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.setCode = this.setCode.bind(this);
	}

	handleSubmit(values: FormValues, actions: FormikHelpers<FormValues>): void {
		const { resetForm } = actions;
		const cnt = this.state.counter + 1;
		this.setState({ isLoading: true });

		request('registration', {
			login: values.email,
			captcha: values.captcha,
			code: this.state.code,
		}).then(() => {
			this.setState({
				code: '',
				success: true,
				message: '',
				isLoading: false,
				counter: cnt,
			});

			resetForm();
		}).catch((e) => {
			this.setState({
				isLoading: false,
				message: e,
				counter: cnt,
			});
		});
	}

	setCode(code: string): void {
		this.setState({
			code,
		});
	}

	private emailField = (): JSX.Element => (
		<FormikField fieldName="email" inputProps={{ placeholder: 'Email' }} />
	);

	private serverMessageRender(): JSX.Element | undefined {
		if (this.state.message) {
			return (<div className="alert alert-danger">{this.state.message}</div>);
		}

		return undefined;
	}

	private captchaField(): JSX.Element {
		return (
			<Field name="captcha">
				{({ field, form }: FieldProps<string, FormValues>) => (
					<div className={`form-group ${form.touched.captcha && form.errors.captcha ? 'has-error' : ''}`}>
						<div className="is-relative">
							<Captcha update={this.setCode} counter={this.state.counter} />
							<Input id="captcha" placeholder="Enter the symbols" {...field} />
							{(form.touched.captcha && form.errors.captcha)
								? <div className="validation-message">{form.errors.captcha}</div>
								: ''}
						</div>
					</div>
				)}
			</Field>
		);
	}

	private innerRender() {
		if (this.state.success) {
			return (
				<div className="text-center card">
					<div className="card__body">
						<h3>An email with further instructions was sent</h3>
						<br />
						<Link to="/login" className="btn btn-success btn-block">To Login Page</Link>
					</div>
				</div>
			);
		}

		return (
			<div className="enter-page__form card">
				<div className="card__body">
					<Formik
						initialValues={{ email: '', captcha: '' }}
						onSubmit={this.handleSubmit}
						validationSchema={validationSchema}
					>
						{() => (
							<Form>
								{this.emailField()}
								{this.captchaField()}
								<div className="text-center form-group">
									<Button
										block
										type="primary"
										htmlType="submit"
										loading={this.state.isLoading}
									>
										Register
									</Button>
								</div>
							</Form>
						)}
					</Formik>
					{this.serverMessageRender()}
				</div>
			</div>
		);
	}

	public render(): JSX.Element {
		return (
			<div className="recover-page enter-page">
				<Helmet>
					<title>Register</title>
				</Helmet>
				<Link to="/"><img src={require('@images/logo.png')} alt="Logo" className="enter-page__logo" /></Link>
				{this.innerRender()}
			</div>
		);
	}
}
